import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { formsEdgeErrorMessage, formsEdgeHeaders, formsEdgeUrl } from '@/lib/formsEdge';
import { FORMS_SSO_DASHBOARD_PATH } from '@/lib/formsUrls';
import type { FormsStaffStoredRole } from '@/lib/formsStaffAccess';

export type FormsStaffAccessRow = {
  id: string;
  clinic_id: string;
  user_id: string;
  role: FormsStaffStoredRole;
  granted_by: string | null;
  granted_at: string;
  sync_status: string;
  sync_error: string | null;
  revoked_at: string | null;
};

export const formsStaffAccessKey = (clinicId: string | null | undefined) =>
  ['clinicForms', 'staffAccess', clinicId ?? null] as const;

function staffEdgeError(
  status: number,
  json: { error?: { message?: string; details?: { reason?: string } } },
  fallback: string,
): string {
  const reason = json.error?.details?.reason;
  if (reason === 'forms_owner') {
    return 'forms_owner';
  }
  if (reason === 'account-in-other-tenant') {
    return 'account-in-other-tenant';
  }
  return formsEdgeErrorMessage(status, json, fallback);
}

export function useFormsStaffAccess(clinicId: string | null | undefined) {
  return useQuery({
    queryKey: formsStaffAccessKey(clinicId),
    enabled: Boolean(clinicId),
    staleTime: 15_000,
    queryFn: async (): Promise<FormsStaffAccessRow[]> => {
      if (!clinicId) return [];
      const { data, error } = await supabase
        .from('clinic_forms_staff_access')
        .select(
          'id, clinic_id, user_id, role, granted_by, granted_at, sync_status, sync_error, revoked_at',
        )
        .eq('clinic_id', clinicId);
      if (error) throw error;
      return (data ?? []) as FormsStaffAccessRow[];
    },
  });
}

export function useGrantFormsStaff(clinicId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      userId: string;
      role?: 'admin' | 'editor';
    }): Promise<{ user_id: string; role: FormsStaffStoredRole }> => {
      if (!clinicId) throw new Error('No clinic selected');
      const { data: session } = await supabase.auth.getSession();
      if (!session.session) throw new Error('Not authenticated');

      const resp = await fetch(formsEdgeUrl('forms-staff-grant'), {
        method: 'POST',
        headers: formsEdgeHeaders(session.session.access_token),
        body: JSON.stringify({
          clinic_id: clinicId,
          user_id: vars.userId,
          role: vars.role,
        }),
      });
      const json = await resp.json() as {
        ok: boolean;
        data?: { user_id: string; role: FormsStaffStoredRole };
        error?: { message?: string; details?: { reason?: string } };
      };
      if (!resp.ok || !json.ok) {
        throw new Error(staffEdgeError(resp.status, json, 'Could not grant Clinio Forms access'));
      }
      return json.data!;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: formsStaffAccessKey(clinicId) });
    },
  });
}

export function useRevokeFormsStaff(clinicId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { userId: string }): Promise<void> => {
      if (!clinicId) throw new Error('No clinic selected');
      const { data: session } = await supabase.auth.getSession();
      if (!session.session) throw new Error('Not authenticated');

      const resp = await fetch(formsEdgeUrl('forms-staff-revoke'), {
        method: 'POST',
        headers: formsEdgeHeaders(session.session.access_token),
        body: JSON.stringify({
          clinic_id: clinicId,
          user_id: vars.userId,
        }),
      });
      const json = await resp.json() as {
        ok: boolean;
        error?: { message?: string; details?: { reason?: string } };
      };
      if (!resp.ok || !json.ok) {
        throw new Error(staffEdgeError(resp.status, json, 'Could not revoke Clinio Forms access'));
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: formsStaffAccessKey(clinicId) });
    },
  });
}

export async function fetchFormsSsoUrl(
  clinicId: string,
  redirectPath: string = FORMS_SSO_DASHBOARD_PATH,
): Promise<string> {
  const { data: session } = await supabase.auth.getSession();
  if (!session.session) throw new Error('Not authenticated');

  const resp = await fetch(formsEdgeUrl('forms-sso-link'), {
    method: 'POST',
    headers: formsEdgeHeaders(session.session.access_token),
    body: JSON.stringify({
      clinic_id: clinicId,
      redirect_path: redirectPath,
    }),
  });
  const json = await resp.json() as {
    ok: boolean;
    data?: { url: string; expires_at: string | null };
    error?: { message?: string; details?: { reason?: string } };
  };
  if (!resp.ok || !json.ok || !json.data?.url) {
    throw new Error(staffEdgeError(resp.status, json, 'Could not open Clinio Forms'));
  }
  return json.data.url;
}
